import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import type { PropertyValues } from 'lit';
import { dispatch } from '../../utils/event-helpers';
import {
  HorizontalTabsController,
  type TabPanelLike,
  type HorizontalTabsHost,
} from './horizontal-tabs.controller';

export type HorizontalTabsVariant = 'underline' | 'button';
export type HorizontalTabsStyle = 'left-aligned' | 'center-aligned' | 'full-width';

/**
 * Horizontal tabbed interface managing a list of `asc-tab-panel` elements.
 *
 * Renders a horizontal row of tab triggers above an associated content region.
 * Enforces single-active-tab behavior, propagates the `variant` to all child
 * panels, and manages ARIA tablist semantics and keyboard roving focus.
 *
 * The content region automatically keeps a `min-height` equal to the tallest
 * panel observed, preventing layout shifts when switching to shorter panels.
 *
 * @element asc-horizontal-tabs
 *
 * @fires asc-change - Fired after any tab activation. Detail: `{ activePanel: HTMLElement; activeIndex: number }`
 *
 * @slot - Default slot. Accepts `asc-tab-panel` elements only.
 *
 * @csspart base - Root vertical layout container.
 * @csspart tablist - The `role="tablist"` nav forming the top row.
 * @csspart tab - Each proxy `<button role="tab">` in the tablist.
 * @csspart tab-label - Text span inside each proxy tab button.
 * @csspart tab-count - Count badge span inside each proxy tab button.
 * @csspart content - Panel projection area wrapping the default slot.
 *
 * @cssprop --asc-horizontal-tabs-gap - Gap between tablist and content. Default: `var(--asc-spacing-3xl)`
 * @cssprop --asc-horizontal-tabs-tablist-bg - Tablist row background. Default: `transparent`
 * @cssprop --asc-horizontal-tabs-content-bg - Content area background. Default: `transparent`
 * @cssprop --asc-horizontal-tabs-tablist-border-color - Tablist bottom border (underline variant). Default: `var(--asc-border-tertiary)`
 * @cssprop --asc-horizontal-tabs-tab-text-color - Default tab text color; Default: `var(--asc-text-tertiary)`. On hover, uses `var(--asc-text-tertiary_on-light)`.
 * @cssprop --asc-horizontal-tabs-tab-bg-hover - Tab hover background. Default: `var(--asc-bg-primary_hover)`
 * @cssprop --asc-horizontal-tabs-tab-padding-x - Tab horizontal padding. Default: `var(--asc-spacing-xl)`
 * @cssprop --asc-horizontal-tabs-tab-padding-y - Tab vertical padding. Default: `var(--asc-spacing-lg)`
 * @cssprop --asc-horizontal-tabs-tab-disabled-text-color - Disabled tab text color. Default: `var(--asc-text-disabled)`
 * @cssprop --asc-horizontal-tabs-count-bg - Count badge background. Default: `var(--asc-bg-primary)`
 * @cssprop --asc-horizontal-tabs-count-text-color - Count badge text color. Default: `var(--asc-text-primary)`
 * @cssprop --asc-horizontal-tabs-count-border-radius - Count badge border radius. Default: `var(--asc-radius-full)`
 * @cssprop --asc-horizontal-tabs-active-underline-color - Active underline color (underline variant). Default: `var(--asc-fg-brand-primary)`
 * @cssprop --asc-horizontal-tabs-active-underline-width - Active underline thickness (underline variant). Default: `2px`
 * @cssprop --asc-horizontal-tabs-active-bg - Active tab background (underline variant). Default: `transparent`
 * @cssprop --asc-horizontal-tabs-active-text-color - Active tab text color (underline variant). Default: `var(--asc-text-brand-secondary)`
 * @cssprop --asc-horizontal-tabs-active-count-bg - Active count badge background. Default: `var(--asc-bg-brand-secondary)`
 * @cssprop --asc-horizontal-tabs-active-count-text-color - Active count badge text color. Default: `var(--asc-text-brand-secondary)`
 * @cssprop --asc-horizontal-tabs-btn-active-bg - Active tab background (button variant). Default: `var(--asc-bg-primary)`
 * @cssprop --asc-horizontal-tabs-btn-active-border-color - Active tab border color (button variant). Default: `var(--asc-border-secondary)`
 * @cssprop --asc-horizontal-tabs-btn-border-radius - Tab border radius (button variant). Default: `var(--asc-radius-sm)`
 * @cssprop --asc-horizontal-tabs-btn-active-text-color - Active tab text color (button variant). Default: `var(--asc-text-primary)`
 */
@customElement('asc-horizontal-tabs')
export class AscHorizontalTabs extends LitElement implements HorizontalTabsHost {
  static override styles = css`
    :host {
      display: block;
    }

    /* ── Base vertical layout ─────────────────────────────────── */
    [part='base'] {
      display: flex;
      flex-direction: column;
      gap: var(--asc-horizontal-tabs-gap, var(--asc-spacing-3xl));
    }

    /* ── Tablist row ──────────────────────────────────────────── */
    [part='tablist'] {
      display: flex;
      align-items: center;
      background: var(--asc-horizontal-tabs-tablist-bg, transparent);
    }

    /* Underline variant: bottom border on tablist */
    :host([variant='underline']) [part='tablist'] {
      border-bottom: var(--asc-border-width, 1px) solid
        var(--asc-horizontal-tabs-tablist-border-color, var(--asc-border-tertiary));
      gap: var(--asc-spacing-lg);
    }

    /* Button variant: container with bg, border, rounded */
    :host([variant='button']) [part='tablist'] {
      background: var(--asc-horizontal-tabs-tablist-bg, var(--asc-bg-secondary));
      border: var(--asc-border-width, 1px) solid
        var(--asc-horizontal-tabs-btn-active-border-color, var(--asc-border-secondary));
      border-radius: var(--asc-radius-lg);
      padding: var(--asc-spacing-xs);
      gap: var(--asc-spacing-xs);
    }

    /* Center-aligned: tabs are grouped and centered in the row */
    :host([tab-style='center-aligned']) [part='tablist'] {
      justify-content: center;
    }

    /* Full-width: tabs stretch evenly */
    :host([tab-style='full-width']) [part='tablist'] {
      justify-content: center;
    }

    :host([tab-style='full-width']) [part='tab'] {
      flex: 1 0 0;
    }

    /* ── Proxy tab button ─────────────────────────────────────── */
    [part='tab'] {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: var(--asc-spacing-md);
      background: none;
      border: none;
      cursor: pointer;
      color: var(--asc-horizontal-tabs-tab-text-color, var(--asc-text-tertiary));
      font-family: var(--asc-font-family-body);
      font-size: var(--asc-font-size-text-sm);
      font-weight: var(--asc-font-weight-semibold);
      line-height: var(--asc-line-height-text-sm);
      white-space: nowrap;
      flex-shrink: 0;
    }

    /* Underline variant tab button */
    :host([variant='underline']) [part='tab'] {
      padding:
        0
        var(--asc-horizontal-tabs-tab-padding-x, var(--asc-spacing-xs))
        var(--asc-horizontal-tabs-tab-padding-y, var(--asc-spacing-lg))
        var(--asc-horizontal-tabs-tab-padding-x, var(--asc-spacing-xs));
      border-bottom: var(--asc-horizontal-tabs-active-underline-width, 2px) solid transparent;
      margin-bottom: calc(-1 * var(--asc-border-width, 1px));
    }

    /* Active underline tab */
    :host([variant='underline']) [part='tab'][aria-selected='true'] {
      border-bottom-color: var(
        --asc-horizontal-tabs-active-underline-color,
        var(--asc-fg-brand-primary)
      );
      background: var(--asc-horizontal-tabs-active-bg, transparent);
      color: var(--asc-horizontal-tabs-active-text-color, var(--asc-text-brand-secondary));
    }

    /* Button variant tab button */
    :host([variant='button']) [part='tab'] {
      padding:
        var(--asc-horizontal-tabs-tab-padding-y, var(--asc-spacing-md))
        var(--asc-horizontal-tabs-tab-padding-x, var(--asc-spacing-lg));
      border-radius: var(--asc-horizontal-tabs-btn-border-radius, var(--asc-radius-sm));
      overflow: hidden;
      position: relative;
    }

    /* Active button tab */
    :host([variant='button']) [part='tab'][aria-selected='true'] {
      background-color: var(--asc-horizontal-tabs-btn-active-bg, var(--asc-bg-primary));
      color: var(--asc-horizontal-tabs-btn-active-text-color, var(--asc-text-primary));
      box-shadow: 0px 1px 2px 0px var(--asc-shadow-xs, rgba(10, 13, 18, 0.05));
    }

    /* Hover */
    [part='tab']:hover:not(:disabled) {
      background-color: var(
        --asc-horizontal-tabs-tab-bg-hover,
        var(--asc-bg-primary_hover)
      );
    }

    :host([variant='underline']) [part='tab']:hover:not(:disabled) {
      color: var(--asc-text-tertiary_on-light);
    }

    /* Focus visible */
    [part='tab']:focus-visible {
      outline: 2px solid var(--asc-focus-ring);
      outline-offset: 2px;
    }

    /* Disabled tab */
    [part='tab']:disabled {
      cursor: not-allowed;
      color: var(
        --asc-horizontal-tabs-tab-disabled-text-color,
        var(--asc-text-disabled)
      );
    }

    [part='tab']:disabled:hover {
      background: none;
    }

    /* ── Tab label ────────────────────────────────────────────── */
    [part='tab-label'] {
      flex-shrink: 0;
    }

    /* ── Tab count badge ──────────────────────────────────────── */
    [part='tab-count'] {
      display: inline-flex;
      align-items: center;
      padding: var(--asc-spacing-xxs) var(--asc-spacing-md);
      background-color: var(--asc-horizontal-tabs-count-bg, var(--asc-bg-primary));
      color: var(--asc-horizontal-tabs-count-text-color, var(--asc-text-primary));
      border: var(--asc-border-width, 1px) solid var(--asc-border-primary);
      border-radius: var(--asc-horizontal-tabs-count-border-radius, var(--asc-radius-full));
      font-size: var(--asc-font-size-text-xs);
      font-weight: var(--asc-font-weight-medium);
      line-height: var(--asc-line-height-text-xs);
      flex-shrink: 0;
    }

    [part='tab'][aria-selected='true'] [part='tab-count'] {
      background-color: var(
        --asc-horizontal-tabs-active-count-bg,
        var(--asc-bg-brand-secondary)
      );
      color: var(
        --asc-horizontal-tabs-active-count-text-color,
        var(--asc-text-brand-secondary)
      );
      border-color: transparent;
    }

    /* ── Content area ─────────────────────────────────────────── */
    [part='content'] {
      background: var(--asc-horizontal-tabs-content-bg, transparent);
    }

    /* ── Hidden utility ───────────────────────────────────────── */
    [hidden] {
      display: none !important;
    }
  `;

  private _controller = new HorizontalTabsController(this);

  /** Visual style applied to the tab list triggers and propagated to child panels. */
  @property({ type: String, reflect: true }) variant: HorizontalTabsVariant = 'underline';

  /** Tab layout distribution. `left-aligned` fits tabs to content width; `center-aligned` centers tabs as a group; `full-width` stretches evenly. */
  @property({ type: String, reflect: true, attribute: 'tab-style' }) tabStyle: HorizontalTabsStyle = 'left-aligned';

  /** Accessible label for the tablist region. */
  @property({ type: String }) label = '';

  @query('[part="content"]') private _contentEl!: HTMLElement;

  @state() private _panels: TabPanelLike[] = [];

  private _contentObserver: ResizeObserver | null = null;
  private _maxContentHeight = 0;
  private _isPreMeasuring = false;

  private async _primeTallestContentHeight(): Promise<void> {
    if (this._isPreMeasuring || !this._contentEl || this._panels.length === 0) return;

    this._isPreMeasuring = true;
    const panels = [...this._panels];
    const originalActive = panels.map(panel => panel.active);
    const originalVisibility = this._contentEl.style.visibility;
    let maxHeight = this._maxContentHeight;

    // Prevent visual flicker while each panel is measured at the current viewport.
    this._contentEl.style.visibility = 'hidden';

    for (let i = 0; i < panels.length; i++) {
      panels.forEach((panel, index) => {
        panel.active = index === i;
      });

      this._panels = [...panels];
      await this.updateComplete;

      const panelEl = (panels[i] as HTMLElement).shadowRoot?.querySelector<HTMLElement>('[part="panel"]');
      if (!panelEl) continue;

      const renderedHeight = panelEl.getBoundingClientRect().height;
      if (renderedHeight > maxHeight) {
        maxHeight = renderedHeight;
      }
    }

    // Restore original active state after pre-measurement.
    panels.forEach((panel, index) => {
      panel.active = originalActive[index];
    });

    this._panels = [...panels];
    await this.updateComplete;

    this._maxContentHeight = maxHeight;
    if (this._maxContentHeight > 0) {
      this._contentEl.style.minHeight = `${this._maxContentHeight}px`;
    }

    this._contentEl.style.visibility = originalVisibility;
    this._isPreMeasuring = false;
  }

  private _updateTallestContentHeight(): void {
    if (!this._contentEl) return;

    // Measure only the currently active panel's rendered height.
    // Using scrollHeight on collapsed/inert panels can over-report image-heavy content.
    const activePanel = this._panels.find(panel => panel.active);
    const panelEl = activePanel
      ? (activePanel as HTMLElement).shadowRoot?.querySelector<HTMLElement>('[part="panel"]')
      : null;

    if (!panelEl) return;

    const renderedHeight = panelEl.getBoundingClientRect().height;
    if (renderedHeight <= 0) return;

    if (renderedHeight > this._maxContentHeight) {
      this._maxContentHeight = renderedHeight;
    }

    if (this._maxContentHeight > 0) {
      this._contentEl.style.minHeight = `${this._maxContentHeight}px`;
    }
  }

  private _setupContentObserver(): void {
    if (this._contentObserver || !this._contentEl) return;
    this._contentObserver = new ResizeObserver(() => {
      this._updateTallestContentHeight();
    });
    this._contentObserver.observe(this._contentEl);
  }

  private _teardownContentObserver(): void {
    this._contentObserver?.disconnect();
    this._contentObserver = null;
  }

  override firstUpdated(): void {
    this._setupContentObserver();
    this._updateTallestContentHeight();
    void this._primeTallestContentHeight();
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this._teardownContentObserver();
  }

  private _syncPanels(): void {
    this._panels = this._controller.syncPanels();
    this._updateTallestContentHeight();
    void this._primeTallestContentHeight();
  }

  private _activatePanel(index: number): void {
    const panels = this._panels;
    if (!panels[index] || panels[index].disabled) return;
    panels.forEach((p, i) => { p.active = i === index; });
    this._panels = [...panels];
    this._updateTallestContentHeight();
    dispatch<{ activePanel: HTMLElement; activeIndex: number }>(
      this, 'asc-change', { detail: { activePanel: panels[index], activeIndex: index } },
    );
  }

  private _handleSlotChange(): void {
    this._syncPanels();
  }

  private _handleAscActivate(e: Event): void {
    const target = e.target as TabPanelLike;
    const index = this._panels.indexOf(target);
    if (index !== -1) this._activatePanel(index);
  }

  private _handleTabClick(index: number): void {
    this._activatePanel(index);
    const buttons = Array.from(
      this.shadowRoot?.querySelectorAll<HTMLButtonElement>('[part="tab"]') ?? [],
    );
    buttons[index]?.focus();
  }

  private _handleTabKeyDown(e: KeyboardEvent): void {
    const panels = this._panels;
    const buttons = Array.from(
      this.shadowRoot?.querySelectorAll<HTMLButtonElement>('[part="tab"]') ?? [],
    );
    const current = buttons.indexOf(e.target as HTMLButtonElement);
    if (current === -1) return;

    const findNonDisabled = (start: number, direction: number): number => {
      let i = (start + direction + panels.length) % panels.length;
      let steps = panels.length;
      while (panels[i].disabled && steps-- > 0) {
        i = (i + direction + panels.length) % panels.length;
      }
      return panels[i].disabled ? current : i;
    };

    let next: number;

    switch (e.key) {
      case 'ArrowRight':
        e.preventDefault();
        next = findNonDisabled(current, 1);
        break;
      case 'ArrowLeft':
        e.preventDefault();
        next = findNonDisabled(current, -1);
        break;
      case 'Home': {
        e.preventDefault();
        const first = panels.findIndex(p => !p.disabled);
        next = first !== -1 ? first : current;
        break;
      }
      case 'End': {
        e.preventDefault();
        let last = current;
        for (let i = panels.length - 1; i >= 0; i--) {
          if (!panels[i].disabled) { last = i; break; }
        }
        next = last;
        break;
      }
      default:
        return;
    }

    this._activatePanel(next);
    buttons[next]?.focus();
  }

  override updated(changed: PropertyValues<this>): void {
    if (changed.has('variant')) {
      this._panels.forEach(p => { p.variant = this.variant; });
    }
    this._updateTallestContentHeight();
  }

  override render() {
    return html`
      <div part="base">
        <nav
          part="tablist"
          role="tablist"
          aria-orientation="horizontal"
          aria-label=${this.label || nothing}
          @keydown=${this._handleTabKeyDown}
        >
          ${this._panels.map((panel, i) => {
            const countText = this._controller.getCountText(panel);
            return html`
              <button
                type="button"
                part="tab"
                role="tab"
                aria-selected="${panel.active}"
                aria-disabled=${panel.disabled ? 'true' : nothing}
                ?disabled=${panel.disabled}
                tabindex=${panel.active ? 0 : -1}
                data-index=${i}
                @click=${() => this._handleTabClick(i)}
              >
                <span part="tab-label">${this._controller.getTabNameText(panel)}</span>
                ${countText
                  ? html`<span part="tab-count">${countText}</span>`
                  : nothing}
              </button>
            `;
          })}
        </nav>

        <!-- Content area -->
        <section part="content">
          <slot
            @slotchange=${this._handleSlotChange}
            @asc-activate=${this._handleAscActivate}
          ></slot>
        </section>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'asc-horizontal-tabs': AscHorizontalTabs;
  }
}
